import { Building2, CheckCircle, Search, TrendingUp } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import type { OnboardingStep } from "~/lib/hooks/use-onboarding";

interface OnboardingWizardProps {
	initialStep: OnboardingStep;
	userName: string | null;
	userEmail: string;
	onUpdateStep: (payload: {
		step?: string;
		useCase?: string;
		name?: string;
		completed?: boolean;
	}) => Promise<void>;
}

const STEPS: OnboardingStep[] = ["welcome", "profile", "use-case", "first-search"];

export function OnboardingWizard({
	initialStep,
	userName,
	userEmail,
	onUpdateStep,
}: OnboardingWizardProps) {
	const navigate = useNavigate();
	const [currentStep, setCurrentStep] = useState<OnboardingStep>(initialStep);
	const [name, setName] = useState(userName ?? "");
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);

	const stepIndex = STEPS.indexOf(currentStep);
	const progress = stepIndex >= 0 ? ((stepIndex + 1) / STEPS.length) * 100 : 100;

	async function goToStep(
		next: OnboardingStep,
		payload?: { step?: string; useCase?: string; name?: string; completed?: boolean },
	) {
		setSaving(true);
		setSaveError(null);
		try {
			await onUpdateStep(payload ?? { step: next });
			setCurrentStep(next);
		} catch {
			setSaveError("Změny se nepodařilo uložit. Zkuste to prosím znovu.");
		} finally {
			setSaving(false);
		}
	}

	async function completeOnboarding(redirectTo = "/dashboard") {
		setSaving(true);
		setSaveError(null);
		try {
			await onUpdateStep({ completed: true });
			navigate(redirectTo);
		} catch {
			setSaveError("Nastavení se nepodařilo dokončit. Zkuste to prosím znovu.");
		} finally {
			setSaving(false);
		}
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
			<div className="w-full max-w-lg mx-4">
				{/* Progress bar */}
				<div className="mb-6">
					<div className="flex justify-between text-xs text-muted-foreground mb-1">
						<span>
							Krok {stepIndex + 1} z {STEPS.length}
						</span>
					</div>
					<div className="h-1.5 w-full rounded-full bg-gray-200">
						<div
							className="h-1.5 rounded-full bg-primary transition-all duration-300"
							style={{ width: `${progress}%` }}
						/>
					</div>
				</div>

				{/* Step content */}
				<div className="rounded-2xl bg-card p-8 shadow-lg">
					{saveError && (
						<p className="mb-4 rounded-lg bg-error/10 px-3 py-2 text-sm text-error" role="alert">
							{saveError}
						</p>
					)}
					{currentStep === "welcome" && (
						<WelcomeStep
							email={userEmail}
							onNext={() => goToStep("profile", { step: "profile" })}
							saving={saving}
						/>
					)}
					{currentStep === "profile" && (
						<ProfileStep
							name={name}
							onNameChange={setName}
							onNext={() =>
								goToStep("use-case", {
									step: "use-case",
									...(name.trim() ? { name: name.trim() } : {}),
								})
							}
							onSkip={() => goToStep("use-case", { step: "use-case" })}
							saving={saving}
						/>
					)}
					{currentStep === "use-case" && (
						<UseCaseStep
							onSelect={async (useCase) => {
								await goToStep("first-search", {
									step: "first-search",
									useCase,
								});
							}}
							saving={saving}
						/>
					)}
					{currentStep === "first-search" && (
						<FirstSearchStep
							onSearch={() => completeOnboarding("/search")}
							onSkip={() => completeOnboarding("/dashboard")}
							saving={saving}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

function WelcomeStep({
	email,
	onNext,
	saving,
}: {
	email: string;
	onNext: () => void;
	saving: boolean;
}) {
	return (
		<div className="flex flex-col items-center gap-6 text-center">
			<img src="/logo.png" alt="FirmIndex" className="h-10" />
			<div>
				<h1 className="text-2xl font-bold">Vítejte ve FirmIndex!</h1>
				<p className="mt-2 text-muted-foreground text-sm">
					Jste přihlášeni jako <span className="font-medium text-foreground">{email}</span>. Za pár
					okamžiků budete mít přístup k finančním datům stovek tisíc českých firem.
				</p>
			</div>
			<div className="grid grid-cols-3 gap-4 w-full text-center text-xs text-muted-foreground">
				<div className="flex flex-col items-center gap-1">
					<Building2 className="h-6 w-6 text-primary" />
					<span>400K+ firem</span>
				</div>
				<div className="flex flex-col items-center gap-1">
					<CheckCircle className="h-6 w-6 text-primary" />
					<span>Denní aktualizace</span>
				</div>
				<div className="flex flex-col items-center gap-1">
					<TrendingUp className="h-6 w-6 text-primary" />
					<span>Finanční analýzy</span>
				</div>
			</div>
			<Button className="w-full text-white" onClick={onNext} disabled={saving}>
				{saving ? "Načítám…" : "Pokračovat"}
			</Button>
		</div>
	);
}

function ProfileStep({
	name,
	onNameChange,
	onNext,
	onSkip,
	saving,
}: {
	name: string;
	onNameChange: (v: string) => void;
	onNext: () => void;
	onSkip: () => void;
	saving: boolean;
}) {
	return (
		<div className="flex flex-col gap-6">
			<div>
				<h2 className="text-xl font-bold">Jak vám máme říkat?</h2>
				<p className="mt-1 text-sm text-muted-foreground">
					Volitelné — jméno zobrazujeme pouze v aplikaci.
				</p>
			</div>
			<div className="flex flex-col gap-2">
				<Label htmlFor="onboarding-name">Jméno</Label>
				<Input
					id="onboarding-name"
					type="text"
					placeholder="Jana Nováková"
					value={name}
					onChange={(e) => onNameChange(e.target.value)}
				/>
			</div>
			<div className="flex gap-3">
				<Button className="flex-1 text-white" onClick={onNext} disabled={saving}>
					{saving ? "Ukládám…" : "Pokračovat"}
				</Button>
				<Button variant="ghost" onClick={onSkip} disabled={saving}>
					Přeskočit
				</Button>
			</div>
		</div>
	);
}

const USE_CASES = [
	{
		key: "supplier-check",
		label: "Prověřuji dodavatele a zákazníky",
		icon: CheckCircle,
	},
	{
		key: "financial-analysis",
		label: "Analyzuji finanční výkonnost firem",
		icon: TrendingUp,
	},
	{
		key: "market-research",
		label: "Zkoumám trh a konkurenci",
		icon: Search,
	},
	{
		key: "investment",
		label: "Investiční due diligence",
		icon: Building2,
	},
] as const;

function UseCaseStep({
	onSelect,
	saving,
}: {
	onSelect: (useCase: string) => Promise<void>;
	saving: boolean;
}) {
	return (
		<div className="flex flex-col gap-6">
			<div>
				<h2 className="text-xl font-bold">K čemu FirmIndex využijete?</h2>
				<p className="mt-1 text-sm text-muted-foreground">Pomůže nám to vylepšit vaši zkušenost.</p>
			</div>
			<div className="flex flex-col gap-2">
				{USE_CASES.map((uc) => (
					<button
						key={uc.key}
						type="button"
						onClick={() => onSelect(uc.key)}
						disabled={saving}
						className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-left text-sm hover:border-primary/50 hover:bg-primary/5 transition-colors disabled:opacity-50"
					>
						<uc.icon className="h-5 w-5 text-primary shrink-0" />
						<span className="font-medium">{uc.label}</span>
					</button>
				))}
			</div>
		</div>
	);
}

function FirstSearchStep({
	onSearch,
	onSkip,
	saving,
}: {
	onSearch: () => Promise<void>;
	onSkip: () => Promise<void>;
	saving: boolean;
}) {
	return (
		<div className="flex flex-col items-center gap-6 text-center">
			<div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
				<Search className="h-8 w-8 text-primary" />
			</div>
			<div>
				<h2 className="text-xl font-bold">Vyzkoušejte první vyhledávání</h2>
				<p className="mt-2 text-sm text-muted-foreground">
					Vyhledejte libovolnou českou firmu podle názvu nebo IČO.
				</p>
			</div>
			<div className="flex flex-col gap-3 w-full">
				<Button className="w-full text-white" onClick={onSearch} disabled={saving}>
					{saving ? "Načítám…" : "Vyhledat firmu"}
				</Button>
				<Button variant="ghost" className="w-full" onClick={onSkip} disabled={saving}>
					Přejít na přehled
				</Button>
			</div>
		</div>
	);
}
